import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Package, Download, Loader2, Calendar, Clock, CheckCircle, 
  XCircle, AlertCircle, CreditCard, Eye, User, ChevronRight,
  Receipt, Banknote, FileText
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import generateQuotePdfDoc from "../../utils/quotePdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString()}`;

const AgentBookings = () => {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [selectedUser, setSelectedUser] = useState("all");
  const [uniqueUsers, setUniqueUsers] = useState([]);
  
  // Payment Details Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
      
      // Extract unique admin/employee names
      const users = [...new Set(response.data
        .filter(b => b.assigned_to_name || b.created_by_name)
        .map(b => b.assigned_to_name || b.created_by_name)
      )];
      setUniqueUsers(users);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadVoucher = async (bookingId) => {
    setDownloading(bookingId);
    try {
      // Use the SAME rich-itinerary PDF that admin gets in Booking Details.
      // Branding comes from this agent's profile — same Voucher PDF, just agent-branded.
      const [bookingRes, profileRes] = await Promise.all([
        axios.get(`${API}/bookings/${bookingId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
      ]);
      const booking = bookingRes.data;
      const me = profileRes.data || {};
      const advisor = {
        name: me.name || '',
        email: me.email || '',
        phone: me.phone || '',
        company_name: me.company_name || 'Happy Holidays DMC',
        designation: me.designation || '',
        address: me.address || '',
        city: me.city || '',
        state: me.state || '',
        website: me.website || '',
        logo_url: me.logo_url || '',
        profile_picture_url: me.profile_picture_url || '',
      };
      const quoteId = booking.quote_id;
      if (!quoteId) { toast.error("No linked quotation found"); return; }
      const quoteRes = await axios.get(`${API}/quotes/${quoteId}`, { headers: { Authorization: `Bearer ${token}` } });
      let q = quoteRes.data;
      // Reflect any booking-level hotel/flight edits in the PDF (room category,
      // meal plan, override hotels etc. saved on the booking).
      if (Array.isArray(booking.hotels_override) && booking.hotels_override.length > 0) {
        q = {
          ...q,
          hotels: booking.hotels_override,
          accommodation_options: (q.accommodation_options && q.accommodation_options.length > 0)
            ? [{ ...q.accommodation_options[0], hotels: booking.hotels_override }, ...q.accommodation_options.slice(1)]
            : [{ hotels: booking.hotels_override }],
        };
      }
      if (Array.isArray(booking.flights_override) && booking.flights_override.length > 0) {
        q = { ...q, flights: booking.flights_override };
      }
      const doc = generateQuotePdfDoc(q, advisor, {
        bookingConfirmations: booking.confirmations || [],
        agentView: true,
        bookingMode: true,
        bookingNumber: booking.booking_number || (booking.id || '').slice(0, 8).toUpperCase(),
      });
      if (!doc) { toast.error("Could not generate voucher"); return; }
      const bookingNo = booking.booking_number || (booking.id || '').slice(0, 8).toUpperCase();
      doc.save(`Booking_${bookingNo}.pdf`);
      toast.success("Voucher downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error(error.response?.data?.detail || "Failed to download voucher");
    } finally {
      setDownloading(null);
    }
  };

  const openPaymentDetails = (booking) => {
    setSelectedBooking(booking);
    setShowPaymentModal(true);
  };

  // Filter bookings by status
  const getFilteredBookings = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let filtered = bookings;
    
    // Filter by user if selected
    if (selectedUser !== "all") {
      filtered = filtered.filter(b => 
        (b.assigned_to_name || b.created_by_name) === selectedUser
      );
    }
    
    switch (activeTab) {
      case "upcoming":
        return filtered.filter(b => {
          if (b.status !== "confirmed") return false;
          const endStr = b.return_date || b.travel_date;
          if (!endStr) return true;
          const endDate = new Date(endStr);
          endDate.setHours(0, 0, 0, 0);
          return endDate >= today;
        }).sort((a, b) => new Date(a.travel_date || 0) - new Date(b.travel_date || 0));
      case "pending":
        return filtered.filter(b => b.status === "pending");
      case "cancelled":
        return filtered.filter(b => b.status === "cancelled");
      case "completed":
        return filtered.filter(b => {
          const returnDate = new Date(b.return_date || b.travel_date);
          return b.status === "confirmed" && returnDate < today;
        }).sort((a, b) => new Date(b.travel_date) - new Date(a.travel_date));
      default:
        return filtered;
    }
  };

  // Get counts for tabs
  const getCounts = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return {
      upcoming: bookings.filter(b => {
        if (b.status !== "confirmed") return false;
        const endStr = b.return_date || b.travel_date;
        if (!endStr) return true;
        const endDate = new Date(endStr);
        endDate.setHours(0, 0, 0, 0);
        return endDate >= today;
      }).length,
      pending: bookings.filter(b => b.status === "pending").length,
      cancelled: bookings.filter(b => b.status === "cancelled").length,
      completed: bookings.filter(b => {
        const returnDate = new Date(b.return_date || b.travel_date);
        return b.status === "confirmed" && returnDate < today;
      }).length
    };
  };

  const counts = getCounts();
  const filteredBookings = getFilteredBookings();

  const tabs = [
    { id: "upcoming", label: "Upcoming", count: counts.upcoming, icon: Calendar, color: "blue" },
    { id: "pending", label: "Pending", count: counts.pending, icon: Clock, color: "amber" },
    { id: "cancelled", label: "Cancelled", count: counts.cancelled, icon: XCircle, color: "red" },
    { id: "completed", label: "Completed", count: counts.completed, icon: CheckCircle, color: "green" }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      confirmed: "bg-emerald-100 text-emerald-700",
      cancelled: "bg-red-100 text-red-700"
    };
    return styles[status] || "bg-gray-100 text-gray-700";
  };

  const getPaymentBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      partial: "bg-orange-100 text-orange-700",
      full: "bg-emerald-100 text-emerald-700"
    };
    return styles[status] || "bg-gray-100 text-gray-700";
  };

  return (
    <DashboardLayout type="agent">
      <div className="space-y-6" data-testid="agent-bookings">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Bookings</h1>
            <p className="text-gray-500">Track your booking status and payments</p>
          </div>
          
          {/* User Filter Dropdown */}
          {uniqueUsers.length > 0 && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="w-[200px] bg-white border-gray-200">
                  <SelectValue placeholder="Filter by User" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {uniqueUsers.map(user => (
                    <SelectItem key={user} value={user}>{user}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const colorClasses = {
              blue: isActive ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-white text-gray-600 border-gray-200 hover:bg-blue-50",
              amber: isActive ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-white text-gray-600 border-gray-200 hover:bg-amber-50",
              red: isActive ? "bg-red-100 text-red-700 border-red-300" : "bg-white text-gray-600 border-gray-200 hover:bg-red-50",
              green: isActive ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-white text-gray-600 border-gray-200 hover:bg-emerald-50"
            };
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all ${colorClasses[tab.color]}`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{tab.label}</span>
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                  isActive ? "bg-white/50" : "bg-gray-100"
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Bookings Table */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No {activeTab} bookings found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Admin / Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Destination</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Travel Date</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking Amt</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Paid Amt</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance Amt</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredBookings.map((booking) => {
                      const nights = booking.nights || 0;
                      const recd = Number(booking.amount_paid || 0);
                      const total = Number(booking.total_amount || 0);
                      const gstOn = !!booking.gst_included;
                      const bal = Number(booking.balance_amount != null ? booking.balance_amount : Math.max(0, total - recd));
                      return (
                      <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <a
                            href={`/agent/bookings/${booking.id}`}
                            className="font-mono text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded text-sm hover:bg-emerald-100 hover:text-emerald-700 transition-colors cursor-pointer"
                            data-testid={`booking-id-${booking.id}`}
                          >
                            #{booking.booking_number || booking.id.slice(0, 8).toUpperCase()}
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-gray-800 font-medium text-sm">
                              {booking.assigned_to_name || booking.created_by_name || 'Admin'}
                            </p>
                            {booking.assigned_user_designation && (
                              <p className="text-gray-400 text-xs">{booking.assigned_user_designation}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-800">{nights > 0 ? `${nights}N ` : ''}{booking.destination}</p>
                          {(() => {
                            const cand = (booking.guest_name || booking.client_name || booking.guest?.name || '').toString().trim();
                            const display = cand && cand !== '.' && cand !== '-' ? cand : '';
                            return display
                              ? <p className="text-emerald-600 text-xs font-medium" data-testid={`booking-client-name-${booking.id}`}>{display}</p>
                              : <p className="text-gray-300 text-xs italic">No guest info</p>;
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-gray-700 text-sm">{formatDate(booking.created_at || booking.booking_date)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-gray-700 text-sm">{formatDate(booking.travel_date)}</span>
                          </div>
                          {booking.return_date && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <Calendar className="w-3.5 h-3.5 text-gray-300" />
                              <span className="text-gray-400 text-xs">{formatDate(booking.return_date)}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-gray-800 font-semibold">{formatCurrency(total)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-emerald-600 font-semibold text-sm">{formatCurrency(recd)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {bal > 0 ? (
                            <span className="text-red-400 font-semibold text-sm">{formatCurrency(bal)}</span>
                          ) : (
                            <span className="text-green-500 font-semibold text-sm">Nil</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`capitalize ${getStatusBadge(booking.status)}`}>
                            {booking.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => openPaymentDetails(booking)}
                            className="group inline-flex items-center gap-1"
                          >
                            <Badge className={`${getPaymentBadge(booking.payment_status)} group-hover:ring-2 ring-offset-1 ring-gray-300 transition-all cursor-pointer capitalize`}>
                              {booking.payment_status}
                            </Badge>
                            <ChevronRight className="w-3 h-3 text-gray-400 group-hover:text-gray-600" />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="inline-flex items-center gap-2">
                            <a href={`/agent/bookings/${booking.id}`}>
                              <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                                <Eye className="w-4 h-4 mr-1" /> Details
                              </Button>
                            </a>
                            {(booking.payment_status === 'full' || booking.agent_download_enabled) && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={downloading === booking.id}
                                onClick={() => downloadVoucher(booking.id)}
                                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                              >
                                {downloading === booking.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <><Download className="w-4 h-4 mr-1" /> Voucher</>
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Info Note */}
        {bookings.some(b => b.payment_status === "pending" || b.payment_status === "partial") && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <p className="text-amber-700 text-sm">
                <strong>Note:</strong> Voucher download will be available after full payment is received and confirmed by admin.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Payment Details Modal */}
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                Payment Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedBooking && (
              <div className="space-y-4">
                {/* Booking Info */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-500 text-sm">Booking ID</span>
                    <span className="font-mono text-emerald-600 font-medium">
                      #{selectedBooking.booking_number || selectedBooking.id.slice(0, 8).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Destination</span>
                    <span className="text-gray-800">{selectedBooking.destination}</span>
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Total Amount{selectedBooking.gst_included ? <span className="text-xs text-gray-400 ml-1">(incl. 5% GST)</span> : ''}</span>
                    <span className="text-xl font-bold text-gray-800">
                      {formatCurrency(selectedBooking.total_amount || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Amount Paid</span>
                    <span className="text-lg font-semibold text-emerald-600">
                      {formatCurrency(selectedBooking.amount_paid || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">Balance Due</span>
                    <span className={`text-lg font-semibold ${
                      (selectedBooking.balance_amount || 0) > 0 
                        ? "text-amber-600" 
                        : "text-emerald-600"
                    }`}>
                      {formatCurrency(selectedBooking.balance_amount || 0)}
                    </span>
                  </div>
                </div>

                {/* Payment History */}
                {selectedBooking.payments && selectedBooking.payments.length > 0 ? (
                  <div>
                    <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      Payment History
                    </h4>
                    <div className="space-y-2">
                      {selectedBooking.payments.map((payment, idx) => (
                        <div key={idx} className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-800">{formatCurrency(payment.amount)}</p>
                              <p className="text-xs text-gray-500">{formatDate(payment.date)}</p>
                            </div>
                            <div className="text-right">
                              <Badge className="bg-blue-100 text-blue-700">
                                {payment.type || payment.payment_type || 'UPI'}
                              </Badge>
                              {payment.transaction_no && (
                                <p className="text-xs text-gray-500 mt-1 font-mono">
                                  Txn: {payment.transaction_no}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-gray-50 rounded-lg">
                    <Banknote className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No payment records found</p>
                    <p className="text-gray-400 text-xs mt-1">Contact admin for payment details</p>
                  </div>
                )}

                {/* Payment Status */}
                <div className="flex justify-center pt-2">
                  <Badge className={`${getPaymentBadge(selectedBooking.payment_status)} px-4 py-1.5 text-sm`}>
                    Payment Status: {selectedBooking.payment_status?.toUpperCase()}
                  </Badge>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AgentBookings;
